import { useState, useCallback, useEffect } from 'react';
import { useApi, ApiConfig } from './useApi';
import { AddressPool, Address, PaginatedResponse } from './types';

export interface UseAddressPoolsOptions {
  autoFetch?: boolean;
  page?: number;
  pageSize?: number;
}

export function useAddressPools(
  config: ApiConfig = {},
  options: UseAddressPoolsOptions = {},
) {
  const { autoFetch = true, page = 1, pageSize = 20 } = options;
  const api = useApi(config);
  const [pools, setPools] = useState<AddressPool[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState<string | null>(null);
  const [previousPage, setPreviousPage] = useState<string | null>(null);

  const fetchPools = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });

      const response = await api.get<PaginatedResponse<AddressPool>>(
        `/ip/pools/?${params}`,
      );
      setPools(response.results);
      setTotalCount(response.count);
      setNextPage(response.next);
      setPreviousPage(response.previous);
      return response;
    } catch (error) {
      console.error('Failed to fetch address pools:', error);
      throw error;
    }
  }, [api, page, pageSize]);

  const getPool = useCallback(
    async (id: number) => {
      return api.get<AddressPool>(`/ip/pools/${id}/`);
    },
    [api],
  );

  const createPool = useCallback(
    async (data: Omit<AddressPool, 'id'>) => {
      const response = await api.post<AddressPool>('/ip/pools/', data);
      await fetchPools();
      return response;
    },
    [api, fetchPools],
  );

  const updatePool = useCallback(
    async (id: number, data: Partial<Omit<AddressPool, 'id'>>) => {
      const response = await api.patch<AddressPool>(`/ip/pools/${id}/`, data);
      await fetchPools();
      return response;
    },
    [api, fetchPools],
  );

  const deletePool = useCallback(
    async (id: number) => {
      await api.delete(`/ip/pools/${id}/`);
      await fetchPools();
    },
    [api, fetchPools],
  );

  const fetchPoolAddresses = useCallback(
    async (poolId: number, addressPage = 1, addressPageSize = 50) => {
      const params = new URLSearchParams({
        page: addressPage.toString(),
        page_size: addressPageSize.toString(),
      });

      return api.get<PaginatedResponse<Address>>(
        `/ip/pools/${poolId}/addresses/?${params}`,
      );
    },
    [api],
  );

  const createAddress = useCallback(
    async (poolId: number, data: Omit<Address, 'id' | 'created_at'>) => {
      return api.post<Address>(`/ip/pools/${poolId}/addresses/`, data);
    },
    [api],
  );

  const updateAddress = useCallback(
    async (
      poolId: number,
      addressId: number,
      data: Partial<Omit<Address, 'id' | 'created_at'>>,
    ) => {
      return api.patch<Address>(
        `/ip/pools/${poolId}/addresses/${addressId}/`,
        data,
      );
    },
    [api],
  );

  const deleteAddress = useCallback(
    async (poolId: number, addressId: number) => {
      return api.delete(`/ip/pools/${poolId}/addresses/${addressId}/`);
    },
    [api],
  );

  const assignAddressToDuck = useCallback(
    async (poolId: number, addressId: number, duckId: number) => {
      return updateAddress(poolId, addressId, { duck: duckId });
    },
    [updateAddress],
  );

  const releaseAddress = useCallback(
    async (poolId: number, addressId: number) => {
      return updateAddress(poolId, addressId, { duck: null });
    },
    [updateAddress],
  );

  useEffect(() => {
    if (autoFetch && config.token) {
      fetchPools();
    }
  }, [autoFetch, config.token, fetchPools]);

  return {
    pools,
    totalCount,
    nextPage,
    previousPage,
    loading: api.loading,
    error: api.error,
    fetchPools,
    getPool,
    createPool,
    updatePool,
    deletePool,
    fetchPoolAddresses,
    createAddress,
    updateAddress,
    deleteAddress,
    assignAddressToDuck,
    releaseAddress,
  };
}
